import React, { useState } from 'react';

// #region ConfirmSelectionsDialogProps
export interface ConfirmSelectionsDialogProps {
    open: boolean;
    onConfirm: () => Promise<void> | void;
    onClose: () => void;
}

// #region ConfirmSelectionsDialog
// Final confirmation before locking the link read-only. Branded, light-mode
// only, and bottom-sheet styled on phones.
export const ConfirmSelectionsDialog: React.FunctionComponent<ConfirmSelectionsDialogProps> = (props: ConfirmSelectionsDialogProps): JSX.Element | null => {
    const { open, onConfirm, onClose } = props;
    const [busy, setBusy] = useState(false);

    const handleConfirm = async () => {
        setBusy(true);
        try {
            await onConfirm();
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6">
            <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="absolute inset-0 cursor-default bg-black/50 backdrop-blur-sm"
            />
            <div
                className="relative z-10 w-full rounded-t-3xl bg-share-raised p-6 text-share-text shadow-2xl sm:max-w-md sm:rounded-3xl"
                style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
            >
                <h2 className="text-lg font-semibold tracking-tight">Confirm your selections?</h2>
                <p className="mt-2 text-sm text-share-muted">
                    Your designer will be notified right away. You won&apos;t be able to change your picks after this.
                </p>
                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="min-h-[48px] rounded-xl px-4 text-sm font-semibold text-share-muted transition hover:bg-share-surface disabled:opacity-50"
                    >
                        Go back
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={busy}
                        style={{ backgroundColor: 'var(--share-primary)', color: 'var(--share-primary-contrast)' }}
                        className="min-h-[48px] rounded-xl px-5 text-sm font-semibold shadow-sm transition hover:brightness-110 disabled:opacity-60"
                    >
                        {busy ? 'Confirming...' : 'Confirm selections'}
                    </button>
                </div>
            </div>
        </div>
    );
};
